import {
  AlertTriangle,
  Clock3,
  FileCode2,
  FileMinus2,
  FilePenLine,
  FilePlus2,
  List,
  Search
} from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Button } from '~/components/common/Button'
import { LoadingState } from '~/components/common/LoadingSpinner'
import DocMarkdown from '~/components/documents/DocMarkdown'
import { useModal } from '~/components/modals/CurieModal'
import CodeDownloadOptions from '~/components/task/CodeDownloadOptions'
import DiffReviewPanel from '~/components/task/DiffReviewPanel'
import PullRequestResult from '~/components/task/PullRequestResult'
import {
  getChangedLineStats,
  getDiffPath,
  getDirectory,
  getFileName
} from '~/components/task/LandingDiffReviewPanel'
import { ViewInAnyPointStudioModal } from '~/components/task/ViewInAnyPointStudioModal'
import {
  LandingTaskPanelBody,
  LandingTaskPanelFrame,
  LandingTaskPanelHeader,
  LandingTaskPanelNotice,
  LandingTaskPanelTab,
  LandingTaskPanelTabs
} from '~/components/task/LandingTaskPanelShell'
import LandingTaskProgressTimeline from '~/components/task/LandingTaskProgressTimeline'
import { RightPanelHorizontalScroll } from '~/components/task/RightPanelHorizontalScroll'
import { Typography } from '~/components/common/Typography'
import { TaskStatuses } from '~/constants/task-status'
import { getCodeDeltaContent, getCodeReviewBriefContent } from '~/hooks/task/useCodeDeltaContent'
import { useTaskDetails } from '~/hooks/task/useTaskDetails'
import { useTaskProgressMessages } from '~/hooks/task/useTaskProgressMessages'
import {
  AgentTaskStatus,
  agentTaskStatusFromJSON,
  CommandType,
  Diff,
  DiffType,
  MessageType,
  messageTypeFromJSON,
  TaskStatusAndRequestResponseTuple
} from '~/protos/agents'
import { TaskObjectWithNoDetails } from '~/types/TaskType'
import { cm } from '~/utils'
import { normalizeCommandType } from '~/task/commandTypeUtils'

type LandingCoderTab = 'progress' | 'diff'
type DiffFileFilter = 'all' | 'added' | 'modified' | 'deleted'

interface LandingCoderTaskPanelProps {
  conversationUuid: string
  onClose?: () => void
  openRequestId?: number
  kickoffContent?: string
  workspaceUuid?: string
  task: TaskObjectWithNoDetails
}

const CODER_COMMANDS: ReadonlySet<CommandType> = new Set([
  CommandType.GENERATE_SIMPLE_CODE_PR,
  CommandType.MULE_VERSION_UPGRADE
])

const CODER_DIFF_BEARING_STATUSES: ReadonlySet<AgentTaskStatus> = new Set([
  AgentTaskStatus.DIFF_GENERATED,
  AgentTaskStatus.DRAFT_GENERATED
])

function extractDiffsFromTuple(tuple: TaskStatusAndRequestResponseTuple): Diff[] | null {
  const typedTuple = tuple as {
    request?: { diff?: Diff[] }
  } & TaskStatusAndRequestResponseTuple
  const responseContent = typedTuple.response?.[0]?.responseContentStream
  return responseContent?.diff || typedTuple.request?.diff || null
}

function isCoderDiffBearingTuple(tuple: TaskStatusAndRequestResponseTuple): boolean {
  const status = agentTaskStatusFromJSON((tuple as { taskStatus: AgentTaskStatus }).taskStatus)
  return CODER_DIFF_BEARING_STATUSES.has(status)
}

function findDiffs(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): Diff[] | null {
  if (targetTuple) {
    const result = extractDiffsFromTuple(targetTuple)
    if (result?.length) return result
    if (isCoderDiffBearingTuple(targetTuple)) return null
  }
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const result = extractDiffsFromTuple(tuples[i])
    if (result?.length) return result
  }
  return null
}

function extractPrLinkFromTuple(tuple: TaskStatusAndRequestResponseTuple | null): string {
  if (!tuple?.response?.length) return ''

  for (let i = tuple.response.length - 1; i >= 0; i -= 1) {
    const stream = tuple.response[i]?.responseContentStream
    if (!stream || messageTypeFromJSON(stream.messageType) !== MessageType.PR) continue

    const prLink = stream.deltaContent?.trim()
    if (prLink) return prLink
  }

  return ''
}

function findPrLink(tuples: TaskStatusAndRequestResponseTuple[]): string {
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const prLink = extractPrLinkFromTuple(tuples[i])
    if (prLink) return prLink
  }

  return ''
}

function findDeltaContent(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): string {
  if (targetTuple) {
    const content = getCodeDeltaContent(targetTuple)
    if (content) return content
    if (isCoderDiffBearingTuple(targetTuple)) return ''
  }
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const content = getCodeDeltaContent(tuples[i])
    if (content) return content
  }
  return ''
}

function findReviewBrief(
  targetTuple: TaskStatusAndRequestResponseTuple | null,
  tuples: TaskStatusAndRequestResponseTuple[]
): string {
  if (targetTuple) {
    const content = getCodeReviewBriefContent(targetTuple)
    if (content) return content
    if (isCoderDiffBearingTuple(targetTuple)) return ''
  }
  for (let i = tuples.length - 1; i >= 0; i -= 1) {
    const content = getCodeReviewBriefContent(tuples[i])
    if (content) return content
  }
  return ''
}

function getCoderDiffTypeConfig(diffType: DiffType) {
  switch (diffType) {
    case DiffType.ADD:
      return {
        icon: FilePlus2,
        pillClass:
          'border-tokens-status-success-2 bg-tokens-status-success-3 text-tokens-status-success-1'
      }
    case DiffType.DELETE:
      return {
        icon: FileMinus2,
        pillClass:
          'border-tokens-status-error-30 bg-tokens-status-error-10 text-tokens-status-error-text'
      }
    case DiffType.MODIFY:
    default:
      return {
        icon: FilePenLine,
        pillClass:
          'border-tokens-status-warning-30 bg-tokens-status-warning-3 text-tokens-status-warning-1'
      }
  }
}

export function isLandingCoderTask(task: TaskObjectWithNoDetails | undefined): boolean {
  if (!task) return false
  return CODER_COMMANDS.has(normalizeCommandType(task.cmd_type))
}

export default function LandingCoderTaskPanel({
  conversationUuid,
  onClose,
  kickoffContent,
  openRequestId,
  workspaceUuid,
  task
}: LandingCoderTaskPanelProps) {
  const taskId = task.task_id
  const fallbackStatus = agentTaskStatusFromJSON(task.status)
  const anyPointStudioModal = useModal()
  const taskDetailsQuery = useTaskDetails(conversationUuid, String(taskId))
  const {
    data: taskDetails,
    isLoading: taskDetailsLoading,
    refetch: refetchTaskDetails
  } = taskDetailsQuery
  const tuples = useMemo(
    () =>
      (taskDetails?.task?.details?.orderedStateResponses ??
        []) as TaskStatusAndRequestResponseTuple[],
    [taskDetails?.task?.details?.orderedStateResponses]
  )

  const detailStatus = taskDetails?.task?.status
    ? agentTaskStatusFromJSON(taskDetails.task.status)
    : AgentTaskStatus.NONE_TASK_STATUS
  const taskStatus =
    detailStatus !== AgentTaskStatus.NONE_TASK_STATUS ? detailStatus : fallbackStatus
  const statusGroup = TaskStatuses[taskStatus]?.group
  const isRunning = statusGroup === 'IN_PROGRESS'
  const { data: progressMessages = [] } = useTaskProgressMessages(
    conversationUuid,
    String(taskId),
    {
      enabled: !!conversationUuid && !!taskId,
      refetchInterval: isRunning ? 10000 : false
    }
  )
  const isReadyForFeedback = taskStatus === AgentTaskStatus.DIFF_GENERATED
  const isTerminal =
    statusGroup === 'COMPLETED' || statusGroup === 'ERRORED' || statusGroup === 'STOPPED'

  const targetTuple = useMemo(() => {
    if (tuples.length === 0) return null
    for (let i = tuples.length - 1; i >= 0; i -= 1) {
      if (isCoderDiffBearingTuple(tuples[i])) return tuples[i]
    }
    return tuples[tuples.length - 1]
  }, [tuples])

  const targetTupleIndex = useMemo(
    () => (targetTuple ? tuples.findIndex(tuple => tuple === targetTuple) : -1),
    [targetTuple, tuples]
  )
  const prLink = useMemo(
    () => (taskStatus === AgentTaskStatus.COMPLETED ? findPrLink(tuples) : ''),
    [taskStatus, tuples]
  )
  const rawDiffs = useMemo(() => findDiffs(targetTuple, tuples), [targetTuple, tuples])
  const diffs = rawDiffs
  const diffFileOptions = useMemo(
    () =>
      (diffs ?? []).map(diff => {
        const path = getDiffPath(diff)
        const { addedLines, removedLines } = getChangedLineStats(diff)
        return {
          addedLines,
          diff,
          directory: getDirectory(path),
          fileName: getFileName(path),
          path,
          removedLines
        }
      }),
    [diffs]
  )
  const [selectedDiffPath, setSelectedDiffPath] = useState('')
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false)
  const [fileQuery, setFileQuery] = useState('')
  const [fileFilter, setFileFilter] = useState<DiffFileFilter>('all')
  const fileMenuRef = useRef<HTMLDivElement | null>(null)
  const deltaContent = useMemo(() => findDeltaContent(targetTuple, tuples), [targetTuple, tuples])
  const reviewBrief = useMemo(() => findReviewBrief(targetTuple, tuples), [targetTuple, tuples])
  const reviewBriefSummary = reviewBrief || (!diffs?.length ? deltaContent : '')
  const headerFileName = useMemo(() => {
    if (selectedDiffPath) return selectedDiffPath
    return diffFileOptions[0]?.path ?? `Coder task #${taskId}`
  }, [diffFileOptions, selectedDiffPath, taskId])
  const headerStatusLabel = statusGroup === 'STOPPED' ? 'Stopped' : null
  const hasPrResult = prLink !== ''
  const hasReviewContent = hasPrResult || !!diffs?.length || !!reviewBriefSummary
  const shouldShowReviewOnly =
    hasReviewContent &&
    !isRunning &&
    (isReadyForFeedback || isTerminal || taskStatus === AgentTaskStatus.DRAFT_GENERATED)
  const shouldDefaultToReview =
    hasReviewContent &&
    (isReadyForFeedback || isTerminal || taskStatus === AgentTaskStatus.DRAFT_GENERATED)

  const defaultTab: LandingCoderTab = shouldDefaultToReview ? 'diff' : 'progress'
  const [activeTab, setActiveTab] = useState<LandingCoderTab>(defaultTab)
  const resultAutoSelectedRef = useRef(false)

  useEffect(() => {
    setSelectedDiffPath(previousPath => {
      const hasPreviousPath = diffFileOptions.some(file => file.path === previousPath)
      if (hasPreviousPath) return previousPath
      return diffFileOptions[0]?.path ?? ''
    })
  }, [diffFileOptions])

  const closeFileMenu = useCallback(() => {
    setIsFileMenuOpen(false)
    setFileQuery('')
    setFileFilter('all')
  }, [])

  const handleSelectDiffPath = useCallback(
    (path: string) => {
      setSelectedDiffPath(path)
      closeFileMenu()
      setActiveTab('diff')
    },
    [closeFileMenu]
  )

  const fileFilterCounts = useMemo(() => {
    return diffFileOptions.reduce(
      (acc, file) => {
        acc.all += 1
        if (file.diff.diffType === DiffType.ADD) acc.added += 1
        else if (file.diff.diffType === DiffType.DELETE) acc.deleted += 1
        else acc.modified += 1
        return acc
      },
      { added: 0, all: 0, deleted: 0, modified: 0 }
    )
  }, [diffFileOptions])

  const fileFilterOptions: Array<{ id: DiffFileFilter; label: string; count: number }> = [
    { id: 'all', label: 'All', count: fileFilterCounts.all },
    { id: 'added', label: 'Added', count: fileFilterCounts.added },
    { id: 'modified', label: 'Modified', count: fileFilterCounts.modified },
    { id: 'deleted', label: 'Deleted', count: fileFilterCounts.deleted }
  ]

  const filteredDiffFileOptions = useMemo(() => {
    const normalizedQuery = fileQuery.trim().toLowerCase()
    return diffFileOptions.filter(file => {
      const matchesQuery = !normalizedQuery || file.path.toLowerCase().includes(normalizedQuery)
      const matchesFilter =
        fileFilter === 'all' ||
        (fileFilter === 'added' && file.diff.diffType === DiffType.ADD) ||
        (fileFilter === 'modified' && file.diff.diffType === DiffType.MODIFY) ||
        (fileFilter === 'deleted' && file.diff.diffType === DiffType.DELETE)
      return matchesQuery && matchesFilter
    })
  }, [diffFileOptions, fileFilter, fileQuery])

  useEffect(() => {
    if (!isFileMenuOpen || typeof document === 'undefined') return

    const handlePointerDown = (event: MouseEvent) => {
      if (!fileMenuRef.current?.contains(event.target as Node)) {
        closeFileMenu()
      }
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeFileMenu()
      }
    }

    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [closeFileMenu, isFileMenuOpen])

  useEffect(() => {
    resultAutoSelectedRef.current = false
    setActiveTab(defaultTab)
  }, [taskId, openRequestId, defaultTab])

  const resultTab: LandingCoderTab | null = hasReviewContent ? 'diff' : null

  useEffect(() => {
    if (isRunning) {
      resultAutoSelectedRef.current = false
      setActiveTab('progress')
      return
    }

    if (!resultAutoSelectedRef.current && resultTab && (isReadyForFeedback || isTerminal)) {
      resultAutoSelectedRef.current = true
      setActiveTab(resultTab)
    }
  }, [isRunning, isReadyForFeedback, isTerminal, resultTab])
  const effectiveActiveTab: LandingCoderTab = shouldShowReviewOnly ? 'diff' : activeTab
  const shouldShowKickoffFallback = !!kickoffContent && !isTerminal && !diffs?.length

  useEffect(() => {
    refetchTaskDetails()
  }, [task.status, task.updated_ts, refetchTaskDetails])

  useEffect(() => {
    if (!isRunning) return
    const intervalId = window.setInterval(() => {
      refetchTaskDetails()
    }, 5000)
    return () => window.clearInterval(intervalId)
  }, [isRunning, refetchTaskDetails])

  const tabs: Array<LandingTaskPanelTab<LandingCoderTab>> = [
    ...(shouldShowReviewOnly
      ? []
      : [
          {
            id: 'progress' as const,
            label: 'Progress',
            icon: <Clock3 className='h-4 w-4' />
          }
        ]),
    {
      id: 'diff',
      label: 'Review',
      icon: <FileCode2 className='h-4 w-4' />,
      disabled: !hasReviewContent
    }
  ]

  const renderKickoffState = () => (
    <div className='flex min-h-0 flex-1 overflow-auto rounded-2xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-6 text-left text-tokens-neutral-text-primary'>
      <DocMarkdown
        text={kickoffContent || ''}
        className='!pl-0 w-full break-words text-[14px] font-medium leading-[180%] text-tokens-neutral-text-secondary [&>p]:!m-0 [&>p+p]:!mt-4 [&_li]:!py-0 [&_li]:!font-medium [&_li]:!leading-[180%] [&_p]:!font-medium [&_p]:!leading-[180%] [&_strong]:!font-bold [&_strong]:!text-tokens-neutral-text-primary [&_table]:!my-4 [&_table]:!w-full [&_table]:!overflow-hidden [&_table]:!rounded-xl [&_table]:!border [&_table]:!border-tokens-neutral-border-2 [&_td]:!border-tokens-neutral-border-2 [&_td]:!px-3 [&_td]:!py-2 [&_th]:!border-tokens-neutral-border-2 [&_th]:!px-3 [&_th]:!py-2'
      />
    </div>
  )

  const renderNoDiffState = () => {
    if (shouldShowKickoffFallback) {
      return renderKickoffState()
    }

    return (
      <div className='flex min-h-0 flex-1 items-center justify-center rounded-2xl border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white p-6 text-center text-tokens-neutral-text-tertiary'>
        No code diff was generated for this task.
      </div>
    )
  }

  const renderWaitingForDiffState = () => {
    if (shouldShowKickoffFallback) {
      return renderKickoffState()
    }

    return (
      <div className='flex h-full items-center justify-center p-6 text-center text-tokens-neutral-text-tertiary'>
        The generated diff will appear here when the coder is ready for review.
      </div>
    )
  }

  const tabContent = () => {
    if (!taskDetails && taskDetailsLoading) {
      return <LoadingState message='Loading coder task...' className='h-full' />
    }

    if (effectiveActiveTab === 'progress') {
      return (
        <RightPanelHorizontalScroll testId='landing-coder-progress-scroll'>
          <LandingTaskProgressTimeline
            emptyDescription='The coder has started, but no progress checkpoints have arrived yet. Larger repositories can take a few minutes.'
            emptyTitle={isRunning ? 'Waiting for coder progress' : 'No coder progress yet'}
            progressMessages={progressMessages}
            isRunning={isRunning}
          />
        </RightPanelHorizontalScroll>
      )
    }

    return hasReviewContent ? (
      <div className='flex h-full min-h-0 flex-col gap-3 overflow-hidden p-4'>
        {hasPrResult ? <PullRequestResult prLink={prLink} className='shrink-0' /> : null}
        {diffs?.length ? (
          <div className='min-h-0 flex-1 overflow-hidden'>
            <DiffReviewPanel
              activePath={selectedDiffPath}
              diffs={diffs}
              hideFileSidebar={true}
              onActivePathChange={setSelectedDiffPath}
            />
          </div>
        ) : (
          renderNoDiffState()
        )}
      </div>
    ) : (
      renderWaitingForDiffState()
    )
  }

  return (
    <LandingTaskPanelFrame>
      <LandingTaskPanelHeader
        onClose={onClose}
        title={
          <div ref={fileMenuRef} className='relative flex h-9 min-w-0 flex-1 items-center gap-2'>
            <button
              type='button'
              onClick={() => {
                if (isFileMenuOpen) {
                  closeFileMenu()
                } else {
                  setIsFileMenuOpen(true)
                }
              }}
              className={cm(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-[12px] text-tokens-neutral-icon-hover transition-colors hover:bg-tokens-neutral-fill-fill-6',
                isFileMenuOpen && 'bg-tokens-neutral-fill-fill-6',
                !diffFileOptions.length && 'cursor-not-allowed opacity-40 hover:bg-transparent'
              )}
              aria-label='Open generated files menu'
              title='Generated files'
              disabled={!diffFileOptions.length}
            >
              <List className='size-4' />
            </button>

            {isFileMenuOpen && diffFileOptions.length > 0 && (
              <div className='landing-diff-file-popover absolute left-0 top-[calc(100%+10px)] z-[80] w-[min(560px,calc(100vw-4rem))] overflow-hidden rounded-lg border border-tokens-neutral-border-2 bg-tokens-neutral-bg-white shadow-popover'>
                <div className='border-b border-tokens-neutral-border-2 bg-tokens-neutral-bg-grey-1 p-2.5'>
                  <div className='relative'>
                    <Search className='pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-tokens-neutral-icon-default' />
                    <input
                      value={fileQuery}
                      onChange={event => setFileQuery(event.target.value)}
                      placeholder='Search changed files'
                      className='h-9 w-full rounded-md border border-tokens-neutral-border-1 bg-tokens-neutral-bg-white pl-8 pr-2 text-xs font-medium text-tokens-neutral-text-primary outline-none transition placeholder:text-tokens-neutral-text-tertiary focus:border-tokens-neutral-border-1 focus:ring-2 focus:ring-tokens-neutral-fill-fill-6'
                    />
                  </div>

                  <div
                    className='mt-2 flex flex-wrap items-center gap-1'
                    aria-label='Filter changed files'
                    data-testid='landing-coder-file-filter'
                  >
                    {fileFilterOptions.map(option => (
                      <button
                        key={option.id}
                        type='button'
                        onClick={() => setFileFilter(option.id)}
                        disabled={option.count === 0}
                        aria-pressed={fileFilter === option.id}
                        className={cm(
                          'flex h-7 items-center gap-1.5 rounded px-2 text-[11px] font-semibold transition-colors',
                          fileFilter === option.id
                            ? 'bg-tokens-neutral-bg-action text-tokens-neutral-text-white shadow-segment'
                            : 'text-tokens-neutral-text-secondary hover:bg-tokens-neutral-fill-fill-4 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent'
                        )}
                      >
                        <span>{option.label}</span>
                        <span
                          className={cm(
                            'font-mono text-[10px] font-semibold',
                            fileFilter === option.id
                              ? 'text-tokens-neutral-text-white'
                              : 'text-tokens-neutral-text-tertiary'
                          )}
                        >
                          {option.count.toLocaleString()}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                <div
                  className='overflow-y-auto overflow-x-hidden overscroll-contain p-1.5 [scrollbar-gutter:stable]'
                  data-testid='landing-coder-file-list'
                  style={{ maxHeight: 'min(440px, calc(100vh - 220px))' }}
                >
                  {filteredDiffFileOptions.length ? (
                    <div className='space-y-1'>
                      {filteredDiffFileOptions.map(file => {
                        const config = getCoderDiffTypeConfig(file.diff.diffType)
                        const FileIcon = config.icon
                        const isSelected = file.path === headerFileName
                        return (
                          <button
                            key={`${file.path}-${file.diff.diffType}`}
                            type='button'
                            onClick={() => handleSelectDiffPath(file.path)}
                            className={cm(
                              'group flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left transition-colors',
                              isSelected
                                ? 'border-tokens-neutral-border-1 bg-tokens-neutral-fill-fill-4'
                                : 'border-transparent hover:border-tokens-neutral-border-2 hover:bg-tokens-neutral-fill-fill-2'
                            )}
                          >
                            <div
                              className={cm(
                                'flex size-6 shrink-0 items-center justify-center rounded-md border',
                                config.pillClass
                              )}
                            >
                              <FileIcon className='h-3.5 w-3.5' />
                            </div>
                            <div className='min-w-0 flex-1'>
                              <div className='truncate text-xs font-semibold text-tokens-neutral-text-primary'>
                                {file.fileName}
                              </div>
                              {file.directory && (
                                <div className='truncate font-mono text-[10px] text-tokens-neutral-text-secondary'>
                                  {file.directory}
                                </div>
                              )}
                            </div>
                            <div className='flex shrink-0 items-center gap-1 font-mono text-[11px] font-semibold'>
                              {file.addedLines > 0 && (
                                <span className='text-tokens-status-success-1'>
                                  +{file.addedLines}
                                </span>
                              )}
                              {file.removedLines > 0 && (
                                <span className='text-tokens-status-error-text'>
                                  -{file.removedLines}
                                </span>
                              )}
                              {file.addedLines === 0 && file.removedLines === 0 && (
                                <span className='font-sans text-[10px] font-medium text-tokens-neutral-text-tertiary'>
                                  0
                                </span>
                              )}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  ) : (
                    <div className='px-3 py-6 text-center text-xs text-tokens-neutral-text-tertiary'>
                      No matching files
                    </div>
                  )}
                </div>
              </div>
            )}
            <Typography
              fontFamily='geist'
              fontSize='bodyMdMedium'
              fontWeight='medium'
              className='min-w-0 truncate text-tokens-neutral-text-primary'
            >
              {headerFileName}
            </Typography>
          </div>
        }
        actions={
          <div
            className='flex shrink-0 items-center justify-end gap-2'
            data-testid='landing-coder-header-actions'
          >
            {diffs?.length ? (
              <CodeDownloadOptions
                diffs={diffs}
                taskId={taskId}
                workspaceUuid={workspaceUuid}
                conversationUuid={conversationUuid}
                tupleIndex={targetTupleIndex >= 0 ? targetTupleIndex : undefined}
                triggerVariant='icon'
              />
            ) : null}
            <Button
              variant='outlined'
              onClick={anyPointStudioModal.open}
              className='h-9 shrink-0 px-4 text-tokens-neutral-bg-action'
            >
              <Typography fontFamily='geist' fontSize='bodyMdMedium' fontWeight='medium'>
                View in AnyPoint Studio
              </Typography>
            </Button>
            {headerStatusLabel && <span className='sr-only'>{headerStatusLabel}</span>}
          </div>
        }
        alert={
          statusGroup === 'ERRORED' ? (
            <div className='mt-3'>
              <LandingTaskPanelNotice
                tone='error'
                icon={<AlertTriangle className='mt-0.5 h-4 w-4 shrink-0' />}
              >
                The coder task errored. The task details may contain the failure message.
              </LandingTaskPanelNotice>
            </div>
          ) : null
        }
      />

      {!shouldShowReviewOnly && (
        <LandingTaskPanelTabs activeTab={effectiveActiveTab} onChange={setActiveTab} tabs={tabs} />
      )}

      <LandingTaskPanelBody>{tabContent()}</LandingTaskPanelBody>

      <ViewInAnyPointStudioModal
        isOpen={anyPointStudioModal.isOpen}
        onClose={anyPointStudioModal.close}
      />
    </LandingTaskPanelFrame>
  )
}
